/**
 * Admin Orcaloops human-wait RPC definitions (OL-81).
 *
 * The operator's inbox: every run of a customer's that is parked waiting for a
 * person. Same operator-surface rules as `orcaloops-outcomes.rpc.ts` — behind
 * `AdminMiddleware`, explicit `customerId`.
 *
 * Pre-created by the seam issue with a single `inbox` rpc; the human-waits
 * issue ADDS `resolveHumanWait` to this same group.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Run } from "@fm/db/models/Run";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

/**
 * The customer whose inbox is being read.
 *
 * @since 1.0.0
 */
export const OutcomeInboxInput = Schema.Struct({
  customerId: Schema.String,
});
export type OutcomeInboxInput = Schema.Schema.Type<typeof OutcomeInboxInput>;

/**
 * One waiting run, as the inbox renders it.
 *
 * Derived from `Run.json` by struct-omit rather than redeclared, so a new
 * column reaches the client without a second edit — but the wire row must NOT
 * be `Run.json` itself, because that carries two secrets:
 *
 * - `deferredToken`, the plaintext `@effect/workflow` DurableDeferred token
 *   which `packages/db/src/tables/runs.ts` declares "never leaves the api" —
 *   holding it lets you resolve the workflow directly; and
 * - `callbackTokenHash`, the run's capability digest.
 *
 * Neither is needed to render an inbox, and both are replayable-capability
 * material, so both are dropped here. Precedent: `SkillArchiveMetaResponse` in
 * `skill-archives.rpc.ts`.
 *
 * @since 1.0.0
 */
export const OutcomeInboxRow = Run.json.pipe(Schema.omit("deferredToken", "callbackTokenHash"));
export type OutcomeInboxRow = Schema.Schema.Type<typeof OutcomeInboxRow>;

/**
 * Answer one waiting run.
 *
 * The payload is `{ customerId, runId }` and NOT a capability token, for two
 * reasons. `admin.outcomes.inbox` omits BOTH `deferredToken` and
 * `callbackTokenHash` from every row it returns, so an operator holds no token
 * to present and a token-keyed rpc would be unusable from the inbox it is meant
 * to serve. And on an AUTHENTICATED surface a customer-scoped read
 * (`findByIdForCustomer`) beats an un-scoped token lookup outright. The
 * `resolveToken` minted into the notification link stays the wave-3 inbox's
 * capability, for the unauthenticated caller who has nothing else.
 *
 * @since 1.0.0
 */
export const ResolveHumanWaitInput = Schema.Struct({
  customerId: Schema.String,
  runId: Schema.String,
  reply: Schema.String,
  option: Schema.optional(Schema.String),
});
export type ResolveHumanWaitInput = Schema.Schema.Type<typeof ResolveHumanWaitInput>;

/**
 * What the resolver wrote: the run it answered and the report it stored.
 *
 * @since 1.0.0
 */
export const ResolveHumanWaitResult = Schema.Struct({
  runId: Schema.String,
  reportId: Schema.String,
});
export type ResolveHumanWaitResult = Schema.Schema.Type<typeof ResolveHumanWaitResult>;

/**
 * Wire form of the service's `HumanWaitNotResolvable` — the run is not a parked
 * human wait, or somebody already answered it.
 *
 * Declared here rather than reusing the service error because the service error
 * is a `Data.TaggedError` (no schema) and every rpc error must be encodable.
 *
 * @since 1.0.0
 */
export class HumanWaitNotResolvableError extends Schema.TaggedError<HumanWaitNotResolvableError>()(
  "HumanWaitNotResolvableError",
  {
    reason: Schema.String,
  },
) {}

/**
 * Admin Orcaloops human-wait RPC group.
 *
 * @since 1.0.0
 */
export class AdminOrcaloopsHumanWaitRpcs extends RpcGroup.make(
  Rpc.make("admin.outcomes.inbox", {
    payload: OutcomeInboxInput,
    success: Schema.Array(OutcomeInboxRow),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.outcomes.resolveHumanWait", {
    payload: ResolveHumanWaitInput,
    success: ResolveHumanWaitResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      HumanWaitNotResolvableError,
      DatabaseError,
    ),
  }).middleware(AdminMiddleware),
) {}
